import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';

import MainLayout from '../layout/MainLayout';
import { featureFlags } from '../config/featureFlags';
import { getUserRoleByToken } from '../utils/commonFuns';
import { getCookie } from '../utils/cookieUtils';
import HCLoader from '../components/HCLoader';

const Users = lazy(() => import('../views/users'));
const ServiceMaster = lazy(() => import('../views/serviceMaster'));
const Reports = lazy(() => import('../views/Reports'));
const PatientVisitCreation = lazy(() => import('../views/patientVisit'));
const Patients = lazy(() => import('../views/patients'));
const NotFound = lazy(() => import('../views/NotFound'));
const LabOrders = lazy(() => import('../views/LabOrders'));
const LabDiagnosticMaster = lazy(() => import('../views/labDiagnosticMaster'));
const ICDCM10Master = lazy(() => import('../views/ICDCM10Master'));
const Facility = lazy(() => import('../views/facility'));
const DrugMaster = lazy(() => import('../views/drugMaster'));
const Dashboard = lazy(() => import('../views/dashboard'));
const Consultation = lazy(() => import('../views/consultation'));
const SignIn = lazy(() => import('../views/auth/SignIn'));
const ErrorBoundary = lazy(() => import('../components/ErrorBoundary'));
const PrivacyPolicy = lazy(() => import('../views/PrivacyPolicy'));
const Units = lazy(() => import('../views/units'));
const Insurance = lazy(() => import('../views/Insurance'));
const InsuranceProviderBeneficiaries = lazy(
    () => import('../views/InsuranceProviderBeneficiaries'),
);
const InsuranceProviderPlans = lazy(
    () => import('../views/InsuranceProviderPlans'),
);
const InsuranceProviderBeneficiaryUsage = lazy(
    () => import('../views/InsuranceProviderBeneficiaryUsage'),
);
const InsuranceProviderFacilities = lazy(
    () => import('../views/InsuranceProviderFacilities'),
);
const IPDAdmission = lazy(() => import('../views/ipdAdmission'));
const FacilityPatientConfiguration = lazy(() => import('../views/facilityPatientConfiguration'));

const isLogin = getCookie('session') ?? '';
const role = getUserRoleByToken(isLogin);

const ipdRoutes = featureFlags.ipdModule
    ? [
          {
              path: 'ipd-admission',
              element: <IPDAdmission />,
          },
          {
              path: 'facility-configuration',
              element: <FacilityPatientConfiguration />,
          },
      ]
    : [];

export const router = createBrowserRouter([
    {
        path: '/',
        element: role ? (
            <ErrorBoundary>
                <MainLayout>
                    <Suspense fallback={<HCLoader />}>
                        <Outlet />
                    </Suspense>
                </MainLayout>
            </ErrorBoundary>
        ) : (
            <ErrorBoundary>
                <Suspense fallback={<HCLoader />}>
                    <SignIn />
                </Suspense>
            </ErrorBoundary>
        ),
        children: [
            {
                path: 'dashboard',
                element: <Dashboard />,
            },
            {
                path: 'facility',
                element: <Facility />,
            },
            {
                path: 'users',
                element: <Users />,
            },
            {
                path: 'units',
                element: <Units />,
            },
            {
                path: 'patients',
                element: <Patients />,
            },
            {
                path: '/patients-visit',
                element: <PatientVisitCreation />,
            },
            {
                path: 'consultation',
                element: <Consultation />,
            },
            {
                path: 'services',
                element: <ServiceMaster />,
            },
            {
                path: 'icd-10',
                element: <ICDCM10Master />,
            },
            {
                path: 'lab-diagnostic',
                element: <LabDiagnosticMaster />,
            },
            {
                path: 'lab-orders',
                element: <LabOrders />,
            },
            {
                path: 'drugs',
                element: <DrugMaster />,
            },
            {
                path: 'reports',
                element: <Reports />,
            },
            {
                path: 'insurance',
                element: <Insurance />,
            },
            {
                path: 'insurance-provider/beneficiaries',
                element: <InsuranceProviderBeneficiaries />,
            },
            {
                path: 'insurance-provider/plans',
                element: <InsuranceProviderPlans />,
            },
            {
                path: 'insurance-provider/beneficiary-usage',
                element: <InsuranceProviderBeneficiaryUsage />,
            },
            {
                path: 'insurance-provider/facilities',
                element: <InsuranceProviderFacilities />,
            },
            ...ipdRoutes,
        ],
    },
    {
        path: '/privacy-policy',
        element: <PrivacyPolicy />,
    },
    {
        path: '*',
        element: <NotFound />,
    },
]);
